import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adminInventoryPurchases,
  adminProducts,
  type InventoryPurchase,
  type InventoryPurchaseInput,
} from '../../api/admin';
import { Plus, Pencil, Trash2, X, ChevronDown, Search, MessageSquare, Download } from 'lucide-react';

const STATUSES: { value: string; label: string }[] = [
  { value: 'order_booked', label: 'Order booked' },
  { value: 'payment_done', label: 'Payment done' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'CNY', 'SGD', 'HKD', 'JPY', 'AUD', 'CAD'];

// Colored badge style per status (mirrors the Orders page inline status select).
const STATUS_STYLE: Record<string, string> = {
  order_booked: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  payment_done: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  in_transit: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  received: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/30',
};

const fmt = (n: number) => `₹${(n ?? 0).toLocaleString('en-IN')}`;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayISO = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
const dateShort = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';

function downloadCSV(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Group a raw numeric string (digits, optional decimal) with Indian commas for display.
const groupIN = (raw: string) => {
  if (!raw) return '';
  const hasDot = raw.includes('.');
  const [intPart, ...rest] = raw.split('.');
  const intClean = intPart.replace(/[^0-9]/g, '');
  const grouped = intClean ? Number(intClean).toLocaleString('en-IN') : '';
  return hasDot ? `${grouped || '0'}.${rest.join('').replace(/[^0-9]/g, '')}` : grouped;
};

export interface ProductLite { id: string; name: string; brand: string; flavor: string | null; strength: number; strength_unit: string; price: number }
interface ItemRow { product_id: string; quantity: string }
interface PForm {
  id: string | null;
  items: ItemRow[];
  no_products: boolean;
  currency: string;
  product_subtotal: string;
  product_subtotal_inr: string;
  shipping_cost: string;
  customs_charges: string;
  amount: string;
  status: string;
  order_date: string;
  received_date: string;
  supplier: string;
  comment: string;
}

const emptyForm: PForm = {
  id: null, items: [{ product_id: '', quantity: '' }], no_products: false,
  currency: 'INR', product_subtotal: '', product_subtotal_inr: '', shipping_cost: '', customs_charges: '',
  amount: '', status: 'order_booked',
  order_date: todayISO(), received_date: '', supplier: '', comment: '',
};

// Searchable product picker (button + dropdown with search + brand tabs), mirroring the Orders edit picker.
export function ProductPicker({ products, value, onSelect, isDark, fieldCls }: {
  products: ProductLite[]; value: string; onSelect: (id: string) => void; isDark: boolean; fieldCls: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const ref = useRef<HTMLDivElement>(null);
  const selected = products.find((p) => p.id === value);
  const brands = useMemo(() => [...new Set(products.map((p) => p.brand))].sort(), [products]);
  const filtered = useMemo(() => {
    let list = products;
    if (brand !== 'all') list = list.filter((p) => p.brand === brand);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || (p.flavor ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [products, brand, search]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch(''); setBrand('all'); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative flex-1 min-w-0" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`${fieldCls} w-full text-left flex items-center justify-between gap-1`}>
        <span className="truncate">{selected ? `${selected.brand} — ${selected.name}` : 'Select a product…'}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-[60] top-full left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minWidth: 260 }}>
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="relative">
              <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
            </div>
          </div>
          <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <button type="button" onClick={() => setBrand('all')} className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${brand === 'all' ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>All</button>
            {brands.map((b) => (
              <button key={b} type="button" onClick={() => setBrand(b)} className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${brand === b ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>{b}</button>
            ))}
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products found</p>}
            {filtered.map((p) => (
              <button key={p.id} type="button" onClick={() => { onSelect(p.id); setOpen(false); setSearch(''); setBrand('all'); }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${p.id === value ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : ''} ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <div className="min-w-0">
                  <p className={`text-xs font-medium truncate ${p.id === value ? 'text-emerald-500' : ''}`}>{p.name}</p>
                  <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand} {'·'} {p.flavor ?? '—'} {'·'} {p.strength}{p.strength_unit}</p>
                </div>
                <span className={`text-xs font-bold shrink-0 ${p.id === value ? 'text-emerald-500' : ''}`}>{fmt(p.price)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PurchasesTab({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<InventoryPurchase[]>([]);
  const [productList, setProductList] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePreset, setDatePreset] = useState<'all' | 'week' | 'month' | 'year' | 'custom'>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modal, setModal] = useState<PForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [receivedPrompt, setReceivedPrompt] = useState<{ id: string; date: string } | null>(null);
  const [receivedError, setReceivedError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleExpand = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const fieldCls = `px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
           : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;
  const inp = `w-full ${fieldCls}`;

  const dateParams = useMemo(() => {
    const iso = (ms: number) => new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);
    const now = Date.now();
    const DAY = 86400000;
    if (datePreset === 'week') return { from: iso(now - 7 * DAY) };
    if (datePreset === 'month') return { from: iso(now - 30 * DAY) };
    if (datePreset === 'year') return { from: iso(now - 365 * DAY) };
    if (datePreset === 'custom') {
      const p: { from?: string; to?: string } = {};
      if (customFrom) p.from = customFrom;
      if (customTo) p.to = customTo;
      return p;
    }
    return {} as { from?: string; to?: string };
  }, [datePreset, customFrom, customTo]);

  const load = useCallback(() => {
    setLoading(true);
    adminInventoryPurchases.list(Object.keys(dateParams).length ? dateParams : undefined)
      .then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [dateParams]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminProducts.list(1, 500)
      .then((r) => setProductList((r.items as unknown as any[]).map((p) => ({
        id: p.id, name: p.name, brand: p.brand, flavor: p.flavor ?? null,
        strength: p.strength, strength_unit: p.strength_unit, price: p.price,
      }))))
      .catch(() => setProductList([]));
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const s of STATUSES) c[s.value] = rows.filter((r) => r.status === s.value).length;
    return c;
  }, [rows]);
  const visibleRows = useMemo(() => (statusFilter === 'all' ? rows : rows.filter((r) => r.status === statusFilter)), [rows, statusFilter]);
  const total = useMemo(() => visibleRows.reduce((s, r) => s + r.amount, 0), [visibleRows]);
  const totalQty = useMemo(() => visibleRows.reduce((s, r) => s + r.items.reduce((q, it) => q + it.quantity, 0), 0), [visibleRows]);

  // ── item-row helpers (modal) ──
  const setItem = (idx: number, patch: Partial<ItemRow>) =>
    setModal((m) => (m ? { ...m, items: m.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) } : m));
  const addItemRow = () => setModal((m) => (m ? { ...m, items: [...m.items, { product_id: '', quantity: '' }] } : m));
  const removeItemRow = (idx: number) => setModal((m) => (m ? { ...m, items: m.items.filter((_, i) => i !== idx) } : m));

  const num = (s: string) => { const n = parseInt(s, 10); return Number.isNaN(n) ? 0 : n; };
  // Update a cost component and auto-recompute the total.
  const updateCost = (patch: Partial<PForm>) => setModal((m) => {
    if (!m) return m;
    const next = { ...m, ...patch };
    const sum = num(next.product_subtotal_inr) + num(next.shipping_cost) + num(next.customs_charges);
    return { ...next, amount: String(sum) };
  });

  // Show an error and scroll the modal back to the top so it's always visible.
  const failWith = (msg: string) => {
    setError(msg);
    requestAnimationFrame(() => modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const save = async () => {
    if (!modal) return;
    let items: { product_id: string; quantity: number }[];
    if (modal.no_products) {
      // General/bulk purchase — no product, but a quantity is required.
      const q = parseInt(modal.items[0]?.quantity ?? '', 10);
      if (Number.isNaN(q) || q <= 0) { failWith('Enter a quantity'); return; }
      items = [{ product_id: '', quantity: q }];
    } else {
      items = modal.items
        .map((it) => ({ product_id: it.product_id, quantity: parseInt(it.quantity, 10) }))
        .filter((it) => it.product_id && !Number.isNaN(it.quantity) && it.quantity > 0);
      if (items.length === 0) { failWith('Add at least one product with a quantity, or tick “No specific products”'); return; }
    }
    const subtotalINR = parseInt(modal.product_subtotal_inr, 10) || 0;
    if (subtotalINR < 0) { failWith('Subtotal in ₹ cannot be negative'); return; }
    if (!modal.product_subtotal_inr.trim()) { failWith('Enter the Subtotal in ₹'); return; }
    const amt = parseInt(modal.amount, 10);
    if (Number.isNaN(amt) || amt < 0) { failWith('Enter a valid total amount'); return; }
    if (!modal.order_date) { failWith('Enter the order date'); return; }
    if (modal.status === 'received' && !modal.received_date) {
      failWith("Can't mark as Received without a received date — pick the received date first.");
      return;
    }
    setSaving(true);
    setError('');
    const payload: InventoryPurchaseInput = {
      items,
      currency: modal.currency,
      product_subtotal: modal.currency === 'INR' ? 0 : (parseFloat(modal.product_subtotal) || 0),
      product_subtotal_inr: num(modal.product_subtotal_inr),
      shipping_cost: num(modal.shipping_cost),
      customs_charges: num(modal.customs_charges),
      amount: amt,
      status: modal.status,
      order_date: modal.order_date || null,
      received_date: modal.status === 'received' ? modal.received_date : (modal.received_date || null),
      supplier: modal.supplier || null,
      comment: modal.comment || null,
    };
    try {
      if (modal.id) await adminInventoryPurchases.update(modal.id, payload);
      else await adminInventoryPurchases.create(payload);
      setModal(null);
      load();
    } catch (e) {
      failWith(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this purchase record?')) return;
    try {
      await adminInventoryPurchases.delete(id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const exportPurchases = () => {
    const header = ['Order date', 'Received date', 'Status', 'Products', 'Currency', 'Subtotal (foreign)', 'Subtotal (INR)', 'Shipping', 'Customs', 'Total', 'Supplier', 'Comment'];
    const csvRows = visibleRows.map((r) => [
      r.order_date ? r.order_date.slice(0, 10) : '',
      r.received_date ? r.received_date.slice(0, 10) : '',
      r.status,
      r.items.map((it) => `${it.product_name} x ${it.quantity}`).join('; '),
      r.currency,
      r.product_subtotal ?? 0,
      r.product_subtotal_inr,
      r.shipping_cost,
      r.customs_charges,
      r.amount,
      r.supplier ?? '',
      r.comment ?? '',
    ]);
    downloadCSV('ZeroSmoke_Purchases.csv', header, csvRows);
  };

  // Inline status change from the table. Choosing "received" opens a date prompt first.
  const changeStatus = async (r: InventoryPurchase, newStatus: string) => {
    if (newStatus === r.status) return;
    if (newStatus === 'received') {
      setReceivedError('');
      setReceivedPrompt({ id: r.id, date: r.received_date ? r.received_date.slice(0, 10) : todayISO() });
      return;
    }
    try {
      await adminInventoryPurchases.update(r.id, { status: newStatus });
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to update status');
      load(); // resync the dropdown to the server state
    }
  };

  const confirmReceived = async () => {
    if (!receivedPrompt || !receivedPrompt.date) return;
    try {
      await adminInventoryPurchases.update(receivedPrompt.id, { status: 'received', received_date: receivedPrompt.date });
      setReceivedPrompt(null);
      setReceivedError('');
      load();
    } catch (e) {
      setReceivedError(e instanceof Error ? e.message : 'Failed to mark as received');
    }
  };

  const openEdit = (r: InventoryPurchase) => {
    const psiINR = r.product_subtotal_inr ?? 0;
    const ship = r.shipping_cost ?? 0;
    const cust = r.customs_charges ?? 0;
    setModal({
      id: r.id,
      items: r.items.length ? r.items.map((it) => ({ product_id: it.product_id ?? '', quantity: String(it.quantity) })) : [{ product_id: '', quantity: '' }],
      no_products: r.items.length === 0 || r.items.every((it) => !it.product_id),
      currency: r.currency || 'INR',
      product_subtotal: r.product_subtotal ? String(r.product_subtotal) : '',
      product_subtotal_inr: psiINR ? String(psiINR) : '',
      shipping_cost: ship ? String(ship) : '',
      customs_charges: cust ? String(cust) : '',
      amount: String(r.amount),
      status: r.status,
      order_date: r.order_date ? r.order_date.slice(0, 10) : todayISO(),
      received_date: r.received_date ? r.received_date.slice(0, 10) : '',
      supplier: r.supplier || '',
      comment: r.comment || '',
    });
    setError('');
  };

  return (
    <div className="space-y-4">
      {/* Add + total */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => { setModal({ ...emptyForm, items: [{ product_id: '', quantity: '' }] }); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg"
        >
          <Plus size={16} /> Add Purchase
        </button>
        <button onClick={exportPurchases}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
          <Download size={15} /> Export CSV
        </button>
        <div className={`ml-auto flex items-center gap-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>Qty: <b>{totalQty.toLocaleString('en-IN')}</b></span>
          <span>Total: <b>{fmt(total)}</b></span>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {[{ value: 'all', label: 'All' }, ...STATUSES].map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              statusFilter === s.value
                ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {s.label} ({counts[s.value] ?? 0})
          </button>
        ))}
      </div>

      {/* Order-date filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order date:</span>
        {([['all', 'All time'], ['week', 'Last week'], ['month', 'Last month'], ['year', 'Last year'], ['custom', 'Custom']] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setDatePreset(v)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              datePreset === v
                ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
                : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>–</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
          </div>
        )}
      </div>

      {/* Table */}
      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
              <th className="font-medium py-3 px-4">Products</th>
              <th className="font-medium py-3 px-4 text-right">Amount</th>
              <th className="font-medium py-3 px-4">Status</th>
              <th className="font-medium py-3 px-4">Order date</th>
              <th className="font-medium py-3 px-4">Received</th>
              <th className="font-medium py-3 px-4">Supplier</th>
              <th className="font-medium py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && visibleRows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-gray-400">No purchases found</td></tr>}
            {visibleRows.map((r) => {
              const open = expandedId === r.id;
              return (
              <Fragment key={r.id}>
              <tr onClick={() => toggleExpand(r.id)} className={`border-b align-top cursor-pointer select-none ${isDark ? 'border-gray-800 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                <td className="py-3 px-4">
                  <div className="space-y-0.5">
                    {r.items.length === 0 && (
                      <div className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No specific products</div>
                    )}
                    {r.items.map((it) => (
                      <div key={it.id} className="text-xs">
                        {it.product_name} <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>× {it.quantity}</span>
                      </div>
                    ))}
                  </div>
                  {r.comment && (
                    <p className={`text-[11px] italic mt-1 flex items-start gap-1 max-w-[240px] break-words ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <MessageSquare size={11} className="mt-0.5 shrink-0" /> {r.comment}
                    </p>
                  )}
                </td>
                <td className="py-3 px-4 text-right font-medium">{fmt(r.amount)}</td>
                <td className="py-3 px-4">
                  <select
                    value={r.status}
                    onChange={(e) => { e.stopPropagation(); changeStatus(r, e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                    className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold outline-none transition-all cursor-pointer ${STATUS_STYLE[r.status] ?? ''}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value} className="text-gray-900">{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-3 px-4">{dateShort(r.order_date)}</td>
                <td className="py-3 px-4">{dateShort(r.received_date)}</td>
                <td className="py-3 px-4"><div className="max-w-[240px] break-words">{r.supplier || '—'}</div></td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} title="Edit" aria-label="Edit" className="text-gray-400 hover:text-emerald-500"><Pencil size={15} /></button>
                    <ChevronDown size={16} className={`transition-transform ${isDark ? 'text-gray-400' : 'text-gray-500'} ${open ? 'rotate-180' : ''}`} />
                    <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} title="Delete" aria-label="Delete" className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
              {open && (
                <tr className={isDark ? 'bg-gray-800/30' : 'bg-gray-50'}>
                  <td colSpan={7} className="px-4 py-4">
                    <div className="flex flex-wrap gap-x-16 gap-y-4 text-sm">
                      {/* Currency */}
                      <div className="flex flex-col gap-2 w-fit">
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Currency</span>
                          <span className="font-medium">{r.currency || 'INR'}</span>
                        </div>
                        {r.currency && r.currency !== 'INR' && (
                          <div className="flex justify-between gap-6">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal ({r.currency})</span>
                            <span className="font-medium">{(r.product_subtotal ?? 0).toLocaleString('en-IN')}</span>
                          </div>
                        )}
                      </div>

                      {/* Cost summary (₹) */}
                      <div className="flex flex-col gap-2 w-fit">
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal (₹)</span>
                          <span>{fmt(r.product_subtotal_inr)}</span>
                        </div>
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                          <span>{fmt(r.shipping_cost)}</span>
                        </div>
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Customs / extra</span>
                          <span>{fmt(r.customs_charges)}</span>
                        </div>
                        <div className={`flex justify-between gap-6 font-bold pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <span>Total</span>
                          <span className="text-emerald-500">{fmt(r.amount)}</span>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="flex flex-col gap-2 w-fit">
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Order date</span>
                          <span className="font-medium">{dateShort(r.order_date)}</span>
                        </div>
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Received date</span>
                          <span className="font-medium">{dateShort(r.received_date)}</span>
                        </div>
                        <div className="flex justify-between gap-6">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Created</span>
                          <span className="font-medium">{dateShort(r.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Supplier + items */}
                    <div className={`mt-4 pt-4 border-t space-y-3 text-sm ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                      <div className="flex gap-2">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Supplier:</span>
                        <span className="font-medium">{r.supplier || '—'}</span>
                      </div>
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Items:</span>
                        <div className="mt-1 space-y-0.5">
                          {r.items.length === 0 && <div className={`italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No specific products</div>}
                          {r.items.map((it) => (
                            <div key={it.id}>{it.product_name} <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>× {it.quantity}</span></div>
                          ))}
                        </div>
                      </div>
                      {r.comment && (
                        <div className="flex gap-2">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Comment:</span>
                          <span>{r.comment}</span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <div ref={modalRef} className={`w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${card}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">{modal.id ? 'Edit' : 'Add'} Purchase</h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                className={`p-1 rounded-lg ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                aria-label="Close"
              ><X size={18} /></button>
            </div>
            {error && <div className="mb-3 text-sm text-red-500">{error}</div>}

            {/* No specific products toggle */}
            <label className={`flex items-start gap-2 mb-3 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <input
                type="checkbox"
                checked={modal.no_products}
                onChange={(e) => { setModal({ ...modal, no_products: e.target.checked }); setError(''); }}
                className="mt-0.5 accent-emerald-500"
              />
              <span>
                No specific products
                <span className={`block text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Tick for a general / bulk purchase or a cost not tied to a product — product selection is disabled.</span>
              </span>
            </label>

            {/* Products — replaced by a single quantity cell when "no specific products" */}
            {modal.no_products ? (
              <div className="mb-3">
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Quantity <span className="text-red-500">*</span></label>
                <input type="number" min={1} value={modal.items[0]?.quantity ?? ''} onChange={(e) => setItem(0, { quantity: e.target.value })} className={inp} placeholder="Qty" />
              </div>
            ) : (
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Products <span className="text-red-500">*</span></label>
                <div className="space-y-2 mb-3">
                  {modal.items.map((it, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <ProductPicker products={productList} value={it.product_id} onSelect={(id) => setItem(idx, { product_id: id })} isDark={isDark} fieldCls={fieldCls} />
                      <input type="number" min={1} value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} className={`${fieldCls} w-20 shrink-0`} placeholder="Qty" />
                      <button
                        type="button"
                        onClick={() => removeItemRow(idx)}
                        disabled={modal.items.length === 1}
                        className={`px-2 rounded-lg ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'} disabled:opacity-30`}
                      ><X size={16} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={addItemRow} className="flex items-center gap-1 text-xs text-emerald-500 hover:text-emerald-400">
                    <Plus size={13} /> Add product
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className={`grid ${modal.currency === 'INR' ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Currency</label>
                  <select value={modal.currency} onChange={(e) => setModal({ ...modal, currency: e.target.value })} className={inp}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {modal.currency !== 'INR' && (
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Subtotal ({modal.currency})</label>
                    <input type="text" inputMode="decimal" value={groupIN(modal.product_subtotal)} onChange={(e) => setModal({ ...modal, product_subtotal: e.target.value.replace(/[^0-9.]/g, '') })} className={inp} placeholder="0" />
                  </div>
                )}
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Subtotal in ₹ <span className="text-red-500">*</span></label>
                  <input type="text" inputMode="numeric" value={groupIN(modal.product_subtotal_inr)} onChange={(e) => updateCost({ product_subtotal_inr: e.target.value.replace(/[^0-9]/g, '') })} className={inp} placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Shipping cost (₹)</label>
                  <input type="text" inputMode="numeric" value={groupIN(modal.shipping_cost)} onChange={(e) => updateCost({ shipping_cost: e.target.value.replace(/[^0-9]/g, '') })} className={inp} placeholder="0" />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Customs / extra (₹)</label>
                  <input type="text" inputMode="numeric" value={groupIN(modal.customs_charges)} onChange={(e) => updateCost({ customs_charges: e.target.value.replace(/[^0-9]/g, '') })} className={inp} placeholder="0" />
                </div>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total (₹) <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>— auto-computed</span></label>
                <input type="text" readOnly value={groupIN(modal.amount)} className={`${inp} opacity-70 cursor-not-allowed`} placeholder="0" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Order date <span className="text-red-500">*</span></label>
                  <input type="date" value={modal.order_date} onChange={(e) => setModal({ ...modal, order_date: e.target.value })} className={inp} />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Received date {modal.status === 'received' && <span className="text-red-500">*</span>}</label>
                  <input type="date" value={modal.received_date} onChange={(e) => setModal({ ...modal, received_date: e.target.value })} className={inp} />
                </div>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</label>
                <select
                  value={modal.status}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModal({ ...modal, status: v });
                    if (v === 'received' && !modal.received_date) failWith("Can't mark as Received without a received date — pick the received date first.");
                    else setError('');
                  }}
                  className={inp}
                >
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Supplier (optional)</label>
                <input type="text" value={modal.supplier} onChange={(e) => setModal({ ...modal, supplier: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Comment (optional)</label>
                <textarea value={modal.comment} onChange={(e) => setModal({ ...modal, comment: e.target.value })} className={inp} rows={2} />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setModal(null)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Received-date prompt (from inline status change) */}
      {receivedPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setReceivedPrompt(null); setReceivedError(''); }}>
          <div className={`w-full max-w-sm rounded-2xl border p-6 ${card}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">Mark as Received</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Enter the date this purchase arrived.</p>
            <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Received date</label>
            <input type="date" value={receivedPrompt.date} onChange={(e) => setReceivedPrompt({ ...receivedPrompt, date: e.target.value })} className={inp} />
            {receivedError && <div className="mt-3 text-sm text-red-500">{receivedError}</div>}
            <div className="flex gap-2 mt-5">
              <button onClick={confirmReceived} disabled={!receivedPrompt.date} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">Confirm</button>
              <button onClick={() => { setReceivedPrompt(null); setReceivedError(''); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
